import classNames from "classnames";
import { useDetailsPanelContext } from "PFApp/booking/components/details_panel";
import { CalendarMode, Row } from "PFApp/booking/types";
import { ProfileBasic, RowResource, RowType } from "PFApp/booking/types/row";
import { getProfileName } from "PFCore/helpers/profile";
import { ProjectItem } from "PFCore/hooks/queries/bookings/projects/use_projects_entries";
import React, { JSX } from "react";
import { TFunction, useTranslation } from "react-i18next";

import { useRowIconComponent } from "../../../hooks/use_row_icon_component";
import { getOriginalResource } from "../../helpers";
import { useBookingsOverviewContext } from "../context/bookings_overview_context";
import css from "../side_panel_rows.module.scss";

type RowNameProps = {
  row: Row;
};

const getRowName = (row: Row, calendarMode: CalendarMode, t: TFunction<"bookingModule">): string => {
  if (row.type === RowType.Other) {
    return t("workforce.otherRowName");
  }
  return row.type === RowType.Profile
    ? getProfileName(getOriginalResource(row, calendarMode) as ProfileBasic)
    : (getOriginalResource(row, calendarMode) as Exclude<RowResource, ProfileBasic>)!.name;
};

export const RowName = ({ row }: RowNameProps): JSX.Element => {
  const { calendarMode } = useBookingsOverviewContext();
  const { openDetailsPanel } = useDetailsPanelContext();
  const { t } = useTranslation("bookingModule");
  const { type, original } = row;
  const name = getRowName(row, calendarMode, t);
  const RowIconComponent = useRowIconComponent({
    templateId: ![RowType.Profile, RowType.Other].includes(type)
      ? (original as ProjectItem).activity.template_id
      : null,
    type
  });
  const isOther = type === RowType.Other;
  const isWorkforceMode = calendarMode === CalendarMode.Workforce;
  const shouldDisplayActivityIcon =
    !isOther && !(isWorkforceMode && type === RowType.Profile) && RowIconComponent;

  const handleClick = () => {
    if (isOther) {
      return;
    }
    openDetailsPanel({
      type,
      id: getOriginalResource(row, calendarMode)?.id!,
      original
    });
  };

  return (
    <button className={classNames(css.name, { [css.disabled]: isOther })} onClick={handleClick}>
      <div className={css.rowNameContainer}>
        {shouldDisplayActivityIcon && (
          <RowIconComponent
            iconProps={{
              width: 20,
              height: 21
            }}
            wrapperProps={{
              classes: {
                container: css.auditActivityIconContainer,
                badge: css.auditActivityIconBadge
              },
              badgeIconProps: { width: 8 }
            }}
          />
        )}
        <div className={css.rowName}>{name}</div>
      </div>
    </button>
  );
};
